import * as React from "react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import Button, { Color } from "../../../../../components/Form/Button";
import BusinessStepper from "../BusinessStepper";
import RowOption from "./RowOption";
import RowCheckOption from "./RowCheckOption";
import { areaLabelSx, headingSx, subheadingSx, questionLabelSx } from "./style";
import { commonStyles } from "../../tokens";

export type FourthStepHandle = {
  videoSurveys?: "Yes" | "No" | null;
  salesReps?: "1-10" | "11-20" | "Above 30" | null;

  storage?: "Yes" | "No" | null;
  warehouses?: "1-10" | "11-30" | "Above 30" | null;

  shipmentTypes?: Array<"local" | "international" | "interstate" | "military">;
  shipmentVolume?: "1-4" | "5-600" | "601-10000" | "10001-20000" | "20001+" | null;
};

type Props = {
  isLoading?: boolean;
  objectPreview?: any;
  onPreviousStep: () => void;
  handleNextStep: () => void;
};

const ORDER: Array<"sales" | "operations" | "warehousing"> = [
  "sales",
  "operations",
  "warehousing",
];

const FourthStep = forwardRef<FourthStepHandle, Props>(function FourthStep(
  { isLoading, objectPreview, onPreviousStep, handleNextStep },
  ref
) {
  const { t: translate } = useTranslation();

  const selectedAreas = useMemo(() => {
    const raw = objectPreview?.areas;
    if (Array.isArray(raw)) return ORDER.filter((k) => raw.includes(k));
    if (raw && typeof raw === "object") return ORDER.filter((k) => !!raw[k]);
    return [];
  }, [objectPreview?.areas]);

  useEffect(() => {
    if (!selectedAreas.length) handleNextStep?.();
  }, [selectedAreas.length, handleNextStep]);

  const [activeIdx, setActiveIdx] = useState(0);
  const currentArea = selectedAreas[activeIdx];

  const [videoSurveys, setVideoSurveys] = useState<"Yes" | "No" | null>(
    objectPreview?.videoSurveys ?? null
  );
  const [salesReps, setSalesReps] = useState<"1-10" | "11-20" | "Above 30" | null>(
    objectPreview?.salesReps ?? null
  );

  const [storage, setStorage] = useState<"Yes" | "No" | null>(
    objectPreview?.storage ?? null
  );
  const [warehouses, setWarehouses] = useState<"1-10" | "11-30" | "Above 30" | null>(
    objectPreview?.warehouses ?? null
  );

  const [shipmentTypes, setShipmentTypes] = useState<
    Array<"local" | "international" | "interstate" | "military">
  >(objectPreview?.shipmentTypes ?? []);
  const [shipmentVolume, setShipmentVolume] = useState<
    "1-4" | "5-600" | "601-10000" | "10001-20000" | "20001+" | null
  >(objectPreview?.shipmentVolume ?? null);

  const [touched, setTouched] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      videoSurveys,
      salesReps,
      storage,
      warehouses,
      shipmentTypes,
      shipmentVolume,
    }),
    [videoSurveys, salesReps, storage, warehouses, shipmentTypes, shipmentVolume]
  );

  const isCurrentAreaValid = useCallback(() => {
    switch (currentArea) {
      case "sales":
        return !!videoSurveys && !!salesReps;
      case "warehousing":
        return !!storage && !!warehouses;
      case "operations":
        return (shipmentTypes?.length ?? 0) > 0 && !!shipmentVolume;
      default:
        return true;
    }
  }, [currentArea, videoSurveys, salesReps, storage, warehouses, shipmentTypes, shipmentVolume]);

  const goNext = () => {
    if (!isCurrentAreaValid()) {
      setTouched(true);
      return;
    }
    if (activeIdx < selectedAreas.length - 1) {
      setActiveIdx((i) => i + 1);
      setTouched(false);
    } else {
      handleNextStep?.();
    }
  };

  const goBack = () => {
    if (activeIdx > 0) {
      setActiveIdx((i) => i - 1);
      setTouched(false);
    } else {
      onPreviousStep?.();
    }
  };

  const toggleType = (key: "local" | "international" | "interstate" | "military") =>
    setShipmentTypes((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const areaLabel =
    currentArea === "sales"
      ? translate("signup.right.sales")
      : currentArea === "operations"
      ? translate("signup.right.operations")
      : translate("signup.right.warehousing");

  const areaTitle =
    currentArea === "sales"
      ? translate("signup.right.salesStepTitle")
      : currentArea === "operations"
      ? translate("signup.right.operationsStepTitle")
      : translate("signup.right.warehousingStepTitle");

  const areaSubtitle =
    currentArea === "sales"
      ? translate("signup.right.salesStepSubtitle")
      : currentArea === "operations"
      ? translate("signup.right.operationsStepSubtitle")
      : translate("signup.right.warehousingStepSubtitle");

  const STEPPER_SIZE = 100;

  return (
    <>
      <Grid item xs={12}>
        <Box sx={{ position: "relative", height: STEPPER_SIZE }}>
          <Typography sx={areaLabelSx} mb={1}>
            {areaLabel}
          </Typography>

          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: "calc(50% + 6px)",
              height: 2,
              bgcolor: "#0055A4",
              borderRadius: 1,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              right: 0,
              top: "calc(50% + 6px)",
              transform: "translateY(-50%)",
            }}
          >
            <BusinessStepper
              active={currentArea}
              selected={selectedAreas}
              size={STEPPER_SIZE}
              gap={16}
              align="end"
            />
          </Box>
        </Box>

        <Typography sx={headingSx} mt={3} mb={0.5}>
          {areaTitle}
        </Typography>
        <Typography sx={subheadingSx} mb={0}>
          {areaSubtitle}
        </Typography>
      </Grid>

      <Grid item xs={12} mt={3}>
        {currentArea === "sales" && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography sx={questionLabelSx}>
                {translate("signup.right.videoSurveysQuestion")}
                <Typography component="span" color="#D32F2F" ml={0.5}>
                  *
                </Typography>
              </Typography>
              <Stack spacing={0.5}>
                <RowOption
                  label={translate("signup.right.yes")}
                  selected={videoSurveys === "Yes"}
                  onClick={() => setVideoSurveys("Yes")}
                />
                <RowOption
                  label={translate("signup.right.no")}
                  selected={videoSurveys === "No"}
                  onClick={() => setVideoSurveys("No")}
                />
              </Stack>
              {touched && !videoSurveys && (
                <Typography variant="caption" color="#D32F2F">
                  {translate("required", "This field is required")}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography sx={questionLabelSx}>
                {translate("signup.right.salesRepsQuestion")}
                <Typography component="span" color="#D32F2F" ml={0.5}>
                  *
                </Typography>
              </Typography>
              <Stack spacing={0.5}>
                <RowOption
                  label={translate("signup.right.range_1_10")}
                  selected={salesReps === "1-10"}
                  onClick={() => setSalesReps("1-10")}
                />
                <RowOption
                  label={translate("signup.right.range_11_20")}
                  selected={salesReps === "11-20"}
                  onClick={() => setSalesReps("11-20")}
                />
                <RowOption
                  label={translate("signup.right.range_above_30")}
                  selected={salesReps === "Above 30"}
                  onClick={() => setSalesReps("Above 30")}
                />
              </Stack>
              {touched && !salesReps && (
                <Typography variant="caption" color="#D32F2F">
                  {translate("required", "This field is required")}
                </Typography>
              )}
            </Grid>
          </Grid>
        )}

        {currentArea === "warehousing" && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography sx={questionLabelSx}>
                {translate("signup.right.storageQuestion")}
                <Typography component="span" color="#D32F2F" ml={0.5}>
                  *
                </Typography>
              </Typography>
              <Stack spacing={0.5}>
                <RowOption
                  label={translate("signup.right.yes")}
                  selected={storage === "Yes"}
                  onClick={() => setStorage("Yes")}
                />
                <RowOption
                  label={translate("signup.right.no")}
                  selected={storage === "No"}
                  onClick={() => setStorage("No")}
                />
              </Stack>
              {touched && !storage && (
                <Typography variant="caption" color="#D32F2F">
                  {translate("required", "This field is required")}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography sx={questionLabelSx}>
                {translate("signup.right.warehousesCountQuestion")}
                <Typography component="span" color="#D32F2F" ml={0.5}>
                  *
                </Typography>
              </Typography>
              <Stack spacing={0.5}>
                <RowOption
                  label={translate("signup.right.wh_1_10", "1-10")}
                  selected={warehouses === "1-10"}
                  onClick={() => setWarehouses("1-10")}
                />
                <RowOption
                  label={translate("signup.right.wh_11_30", "11-30")}
                  selected={warehouses === "11-30"}
                  onClick={() => setWarehouses("11-30")}
                />
                <RowOption
                  label={translate("signup.right.wh_above_30", "Above 30")}
                  selected={warehouses === "Above 30"}
                  onClick={() => setWarehouses("Above 30")}
                />
              </Stack>
              {touched && !warehouses && (
                <Typography variant="caption" color="#D32F2F">
                  {translate("required", "This field is required")}
                </Typography>
              )}
            </Grid>
          </Grid>
        )}

        {currentArea === "operations" && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography sx={questionLabelSx}>
                {translate("signup.right.shipmentType")}
                <Typography component="span" color="#D32F2F" ml={0.5}>
                  *
                </Typography>
              </Typography>
              <Stack spacing={0.5}>
                <RowCheckOption
                  label={translate("signup.right.local")}
                  selected={shipmentTypes.includes("local")}
                  onClick={() => toggleType("local")}
                />
                <RowCheckOption
                  label={translate("signup.right.international")}
                  selected={shipmentTypes.includes("international")}
                  onClick={() => toggleType("international")}
                />
                <RowCheckOption
                  label={translate("signup.right.interstate")}
                  selected={shipmentTypes.includes("interstate")}
                  onClick={() => toggleType("interstate")}
                />
                <RowCheckOption
                  label={translate("signup.right.military")}
                  selected={shipmentTypes.includes("military")}
                  onClick={() => toggleType("military")}
                />
              </Stack>
              {touched && (!shipmentTypes || shipmentTypes.length === 0) && (
                <Typography variant="caption" color="#D32F2F">
                  {translate("required", "This field is required")}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography sx={questionLabelSx}>
                {translate("signup.right.annualShipmentVolume")}
                <Typography component="span" color="#D32F2F" ml={0.5}>
                  *
                </Typography>
              </Typography>
              <Stack spacing={0.5}>
                <RowOption
                  label={translate("signup.right.v_1_4", "1-4")}
                  selected={shipmentVolume === "1-4"}
                  onClick={() => setShipmentVolume("1-4")}
                />
                <RowOption
                  label={translate("signup.right.v_5_600", "5-600")}
                  selected={shipmentVolume === "5-600"}
                  onClick={() => setShipmentVolume("5-600")}
                />
                <RowOption
                  label={translate("signup.right.v_601_10000", "601 - 10,000")}
                  selected={shipmentVolume === "601-10000"}
                  onClick={() => setShipmentVolume("601-10000")}
                />
                <RowOption
                  label={translate("signup.right.v_10001_20000", "10,001 - 20,000")}
                  selected={shipmentVolume === "10001-20000"}
                  onClick={() => setShipmentVolume("10001-20000")}
                />
                <RowOption
                  label={translate("signup.right.v_20001_plus", "20,001 +")}
                  selected={shipmentVolume === "20001+"}
                  onClick={() => setShipmentVolume("20001+")}
                />
              </Stack>
              {touched && !shipmentVolume && (
                <Typography variant="caption" color="#D32F2F">
                  {translate("required", "This field is required")}
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
      </Grid>

      <Grid item xs={12} container spacing={2} mt={3}>
        <Grid item xs={12} sm={6}>
          <Button
            fullWidth
            variant="outlined"
            onClick={goBack}
            disabled={!!isLoading}
            data-cy="backButton"
            aria-label="backButton"
            sx={commonStyles.backButton}
          >
            {translate("signup.right.back")}
          </Button>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            fullWidth
            customColor={Color.SECONDARY_DARK}
            onClick={goNext}
            disabled={!isCurrentAreaValid()}
            sx={{
              height: 40,
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            {translate("signup.right.next")}
          </Button>
        </Grid>
      </Grid>
    </>
  );
});

export default FourthStep;
